import { useContext, useEffect, useState, FC } from 'react';
import { MPosContext } from 'utils/context';
import * as Styles from './styles';
import { useRouter } from 'next/router';
import { IOrderDetails, IPaymentOption, PaymentOptionType } from './types';
import SnackBar from 'library/snackbar';
import ErrorPage from 'widgets/errorScreen';
import { PageLoader } from 'utils/styles';
import { useFetchParams } from 'hooks/useFetchParams';
import { getImageBasePath } from 'utils/generics';
import { PrimaryButton } from '@acko-tech/building-blocks.ui.button';
import { ModalMobile, ModalDesktop } from '@acko-tech/building-blocks.ui.modal';
import { useResponsiveDevice } from 'hooks/useResponsiveDevice';
import PlansHeader from 'components/newPlan/plans/header';
import { NewPlanStages } from 'components/newPlan/constants';

interface WarningIconProps {
    size?: number;
    color?: string;
}

const WarningIcon: FC<WarningIconProps> = ({
    size = 24,
    color = '#F5A623',
}) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <path
            d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
            stroke={color}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const OrderSummary = () => {
    const router = useRouter();
    const { lob } = useFetchParams();
    const { isMobile } = useResponsiveDevice();
    const [isLoading, setIsLoading] = useState(true);
    const { partnerId, planDetails, permissions } = useContext(MPosContext);
    const [snackbar, setSnackbar] = useState({
        type: '',
        message: '',
    });
    const [orderDetails, setOrderDetails] = useState<IOrderDetails | null>(
        null
    );
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [paymentOptions, setPaymentOptions] = useState<IPaymentOption[]>([]);
    const { metaData, setActiveStage } = useContext(MPosContext);
    const { promoter_id_visibility } = metaData;
    const [buttonLoader, setButtonLoaders] = useState({});
    const [showEmiConfirmation, setShowEmiConfirmation] = useState(false);
    const [pendingPaymentType, setPendingPaymentType] =
        useState<PaymentOptionType | null>(null);

    const Modal: any = isMobile ? ModalMobile : ModalDesktop;

    const getOrders = async () => {
        import('./apis').then(async (req) => {
            const { status, data, statusCode } = await req.getOrderByOrderEkey(
                partnerId,
                planDetails.order_ekey
            );
            if (statusCode === 401) {
                router.push('/login');
                setSnackbar({
                    type: 'failure',
                    message: 'Token expired. Redirecting to login page.',
                });
                return;
            }
            await getAllPaymentOptions();
            setIsLoading(false);
            if (status && data?.recent_orders?.length > 0) {
                setOrderDetails(data.recent_orders[0]);
                setErrorMessage('');
            } else {
                setErrorMessage('Oops. something went wrong.');
            }
        });
    };

    const getAllPaymentOptions = async () => {
        import('./apis').then(async (req) => {
            const { data, errorMessage, statusCode } =
                await req.getAllPaymentOptions(partnerId, permissions.group);
            if (statusCode === 400) {
                setSnackbar({
                    type: 'failure',
                    message: errorMessage || '',
                });
            }
            if (statusCode === 401) {
                router.push('/login');
                setSnackbar({
                    type: 'failure',
                    message: 'Token expired. Redirecting to login page.',
                });
                return;
            }
            setPaymentOptions([...data]);
            data.forEach((paymentOption: IPaymentOption) => {
                setButtonLoaders((buttonLoader) => ({
                    ...buttonLoader,
                    [paymentOption.type]: false,
                }));
            });
        });
    };

    useEffect(() => {
        getOrders();
    }, []);

    const onPayNow = async (type: string) => {
        setButtonLoaders((buttonLoader) => ({
            ...buttonLoader,
            [type]: true,
        }));
        import('./apis').then(async (req) => {
            const { data, status, statusCode } = await req.dealerPayment(
                partnerId,
                planDetails.order_ekey
            );
            if (statusCode === 401) {
                router.push('/login');
                setSnackbar({
                    type: 'failure',
                    message: 'Token expired. Redirecting to login page.',
                });
                return;
            }
            if (status && data) {
                if (status && data?.payment_gateway_url) {
                    window.location.href = `${data.payment_gateway_url}?id=${data?.payment_order_ekey}&redirect_source=pos_dealer`;
                }
                return;
            }
            setSnackbar({
                type: 'failure',
                message: 'Oops. Something went wrong.',
            });
            setButtonLoaders((buttonLoader) => ({
                ...buttonLoader,
                [type]: false,
            }));
        });
    };

    const customerPayment = async () => {
        setActiveStage(NewPlanStages.CUSTOMER_LINK);
    };

    const walletPayment = async () => {
        router.push(`/${lob}/${partnerId}/payment-mode`);
    };

    const paymentButtonHandler = async (type: PaymentOptionType) => {
        if (
            orderDetails?.plan?.emi_enabled &&
            orderDetails?.plan?.emi_selected
        ) {
            setPendingPaymentType(type);
            setShowEmiConfirmation(true);
            return;
        }
        await executePayment(type);
    };

    const executePayment = async (type: PaymentOptionType) => {
        if (type === PaymentOptionType.DEALER_PAYMENT) {
            await onPayNow(type);
        } else if (type === PaymentOptionType.CUSTOMER_PAYMENT) {
            await customerPayment();
        } else if (type === PaymentOptionType.DEALER_WALLET_PAYMENT) {
            await walletPayment();
        }
    };

    const handleEmiConfirmation = async () => {
        setShowEmiConfirmation(false);
        if (pendingPaymentType) {
            await executePayment(pendingPaymentType);
        }
        setPendingPaymentType(null);
    };

    const handleEmiCancel = () => {
        setShowEmiConfirmation(false);
        setPendingPaymentType(null);
    };

    if (isLoading) {
        return <PageLoader src={getImageBasePath('/gif/pageLoader.gif')} />;
    }

    if (errorMessage) {
        return <ErrorPage />;
    }

    return (
        <>
            <PlansHeader />
            <Styles.Wrapper>
                <Styles.Container>
                    <Styles.Section>
                        <Styles.Title>Plan summary</Styles.Title>
                        <Styles.Details>
                            <Styles.Row>
                                <Styles.LabelContainer>
                                    <Styles.Label>IMEI</Styles.Label>
                                </Styles.LabelContainer>
                                <Styles.ValueContainer>
                                    <Styles.Value>
                                        {orderDetails?.device.serial_number}
                                    </Styles.Value>
                                </Styles.ValueContainer>
                            </Styles.Row>
                            <Styles.Row>
                                <Styles.LabelContainer>
                                    <Styles.Label>Plan Name</Styles.Label>
                                </Styles.LabelContainer>
                                <Styles.ValueContainer>
                                    <Styles.Value>
                                        {orderDetails?.plan?.partner}{' '}
                                        {orderDetails?.device?.category}{' '}
                                        {orderDetails?.plan?.plan_category}
                                    </Styles.Value>
                                </Styles.ValueContainer>
                            </Styles.Row>
                            <Styles.Row>
                                <Styles.LabelContainer>
                                    <Styles.Label>
                                        What&rsquo;s covered
                                    </Styles.Label>
                                </Styles.LabelContainer>
                                <Styles.ValueContainer>
                                    <Styles.Value>
                                        {orderDetails?.plan.plan_category}{' '}
                                        {/* <Styles.Link href="#">
                                        View Details
                                    </Styles.Link> */}
                                    </Styles.Value>
                                </Styles.ValueContainer>
                            </Styles.Row>
                            <Styles.Row>
                                <Styles.LabelContainer>
                                    <Styles.Label>
                                        Plan price (inc GST)
                                    </Styles.Label>
                                </Styles.LabelContainer>
                                <Styles.ValueContainer>
                                    <Styles.Value>
                                        Rs {orderDetails?.plan.premium}
                                    </Styles.Value>
                                </Styles.ValueContainer>
                            </Styles.Row>
                            {orderDetails?.plan?.commission_amount &&
                            orderDetails?.plan?.commission_amount !== 0 ? (
                                <Styles.Row>
                                    <Styles.LabelContainer>
                                        <Styles.Label>
                                            Dealer Share (inc GST)
                                        </Styles.Label>
                                    </Styles.LabelContainer>
                                    <Styles.ValueContainer>
                                        <Styles.Value>
                                            Rs{' '}
                                            {
                                                orderDetails.plan
                                                    .commission_amount
                                            }
                                        </Styles.Value>
                                    </Styles.ValueContainer>
                                </Styles.Row>
                            ) : null}
                            {orderDetails?.plan?.emi_enabled === true && (
                                <Styles.Row>
                                    <Styles.LabelContainer>
                                        <Styles.Label>
                                            Payment Mode
                                        </Styles.Label>
                                    </Styles.LabelContainer>
                                    <Styles.ValueContainer>
                                        <Styles.Value>
                                            {orderDetails?.plan?.payment_mode}
                                        </Styles.Value>
                                    </Styles.ValueContainer>
                                </Styles.Row>
                            )}
                            {orderDetails?.plan?.discount_amount &&
                            orderDetails?.plan?.discount_amount !== 0 ? (
                                <Styles.Row>
                                    <Styles.LabelContainer>
                                        <Styles.Label>
                                            Discount (inc GST)
                                        </Styles.Label>
                                    </Styles.LabelContainer>
                                    <Styles.ValueContainer>
                                        <Styles.Value>
                                            Rs{' '}
                                            {
                                                orderDetails?.plan
                                                    ?.discount_amount
                                            }
                                        </Styles.Value>
                                    </Styles.ValueContainer>
                                </Styles.Row>
                            ) : null}
                            <Styles.Row>
                                <Styles.LabelContainer>
                                    <Styles.Label>Settle to Acko</Styles.Label>
                                </Styles.LabelContainer>
                                <Styles.ValueContainer>
                                    <Styles.Value>
                                        Rs {orderDetails?.plan.settle_with_acko}
                                    </Styles.Value>
                                </Styles.ValueContainer>
                            </Styles.Row>
                        </Styles.Details>
                    </Styles.Section>
                    <Styles.Section>
                        <Styles.Title>Customer details</Styles.Title>
                        <Styles.Details>
                            <Styles.Row>
                                <Styles.LabelContainer>
                                    <Styles.Label>Name</Styles.Label>
                                </Styles.LabelContainer>
                                <Styles.ValueContainer>
                                    <Styles.Value>
                                        {orderDetails?.customer.name}
                                    </Styles.Value>
                                </Styles.ValueContainer>
                            </Styles.Row>
                            <Styles.Row>
                                <Styles.LabelContainer>
                                    <Styles.Label>Mobile</Styles.Label>
                                </Styles.LabelContainer>
                                <Styles.ValueContainer>
                                    <Styles.Value>
                                        {orderDetails?.customer.phone}
                                    </Styles.Value>
                                </Styles.ValueContainer>
                            </Styles.Row>
                            <Styles.Row>
                                <Styles.LabelContainer>
                                    <Styles.Label>Email</Styles.Label>
                                </Styles.LabelContainer>
                                <Styles.ValueContainer>
                                    <Styles.Value>
                                        {orderDetails?.customer.email || 'NA'}
                                    </Styles.Value>
                                </Styles.ValueContainer>
                            </Styles.Row>
                        </Styles.Details>
                    </Styles.Section>
                    {promoter_id_visibility && (
                        <Styles.Section>
                            <Styles.Title>Promoter details</Styles.Title>
                            <Styles.Details>
                                <Styles.Row>
                                    <Styles.LabelContainer>
                                        <Styles.Label>Promoter Id</Styles.Label>
                                    </Styles.LabelContainer>
                                    <Styles.ValueContainer>
                                        <Styles.Value>
                                            {orderDetails?.device.promoter_id}
                                        </Styles.Value>
                                    </Styles.ValueContainer>
                                </Styles.Row>
                            </Styles.Details>
                        </Styles.Section>
                    )}
                    <Styles.ButtonContainer>
                        {paymentOptions.map((paymentOptions) => (
                            <PrimaryButton
                                key={paymentOptions.type}
                                loading={buttonLoader[paymentOptions.type]}
                                disabled={!paymentOptions.applicable}
                                onClick={() =>
                                    paymentButtonHandler(paymentOptions.type)
                                }
                            >
                                {paymentOptions.name}
                            </PrimaryButton>
                        ))}
                    </Styles.ButtonContainer>
                </Styles.Container>
                <Modal
                    header={<></>}
                    show={showEmiConfirmation}
                    onHide={handleEmiCancel}
                    padding={'20px'}
                    wrapperStyles={`border-radius: 16px;`}
                    showOverlayBackground={true}
                >
                    <Styles.EmiConfirmationWrapper>
                        <Styles.EmiWarningIcon>
                            <WarningIcon />
                        </Styles.EmiWarningIcon>
                        <Styles.EmiConfirmationTitle>
                            Confirm EMI settlement
                        </Styles.EmiConfirmationTitle>
                        <Styles.EmiConfirmationText>
                            Settle ₹{orderDetails?.plan?.settle_with_acko} to
                            ACKO via EMI. Dealer share will be processed
                            separately post-collection.
                        </Styles.EmiConfirmationText>
                        <Styles.EmiConfirmationButtons>
                            <Styles.EmiCancelButton onClick={handleEmiCancel}>
                                No
                            </Styles.EmiCancelButton>
                            <Styles.EmiConfirmButton
                                onClick={handleEmiConfirmation}
                            >
                                Yes, continue
                            </Styles.EmiConfirmButton>
                        </Styles.EmiConfirmationButtons>
                    </Styles.EmiConfirmationWrapper>
                </Modal>
                <SnackBar
                    {...snackbar}
                    handleSnakbarComplete={() =>
                        setSnackbar({ type: '', message: '' })
                    }
                />
            </Styles.Wrapper>
        </>
    );
};

export default OrderSummary;
